import React, { useState, useEffect } from 'react';
import './index.css';
import { Chrono } from 'react-chrono';
import { FaEdit, FaEye, FaTrash, FaSyncAlt, FaStar, FaFileUpload } from 'react-icons/fa';


const JoinedAdmission = () => {
  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editPopup, setEditPopup] = useState(null);
  const [updatePopup, setUpdatePopup] = useState(null);
  const [viewDetailsPopup, setViewDetailsPopup] = useState([]); // ✅ Fixed initialization
  const [feedbackPopup, setFeedbackPopup] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({
    feedback: '',
    reviewerName: '',
    rating: '',
    reviewNumber: '',
    attachment: null
  });
  const [currentPage, setCurrentPage] = useState(1);
 const [itemsPerPage, setItemsPerPage] = useState(10);

  const [editData, setEditData] = useState({});
  const [updateData, setUpdateData] = useState({});
  const [filters, setFilters] = useState({
    projectId: '',
    projectCategory: '',
    guide: '',
    fromDate: '',
    toDate: '',
    searchQuery: '',
    status: '',
  });

  const api = process.env.REACT_APP_API;

  const guidesList = [
    "Vijay","Deepak","Srikanth","Suvarna","Dinesh Kumar",
    "Nagajyothi","Shravani","Aarthi","DurgaPrasad","Rajesh","Nikitha"
  ];

  // Helper function to extract last 4 digits from projectId and convert to number
  const getLastFourDigits = (projectId) => {
    if (!projectId) return 0;
    const projectIdStr = projectId.toString();
    const lastFour = projectIdStr.slice(-4);
    const num = parseInt(lastFour, 10);
    return isNaN(num) ? 0 : num;
  };

  // Fetch Admissions
  useEffect(() => {
    const fetchAdmissions = async () => {
      try {
        const token = localStorage.getItem("jwt_token");
        if (!token) throw new Error("Please login to view projects");

        const response = await fetch(`${api}/project-admissions`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (!Array.isArray(data)) throw new Error("Invalid data format");

        // Fetch project status for each admission to get completedPercentage
        const admissionsWithStatus = await Promise.all(
          data.map(async (admission) => {
            try {
              const statusResponse = await fetch(`${api}/project-status/${admission.projectId}`, {
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
              });

              if (statusResponse.ok) {
                const statusData = await statusResponse.json();
                const latestStatus = Array.isArray(statusData.statusList) && statusData.statusList.length > 0
                  ? statusData.statusList[0]
                  : Array.isArray(statusData.status) && statusData.status.length > 0
                  ? statusData.status[0]
                  : null;

                return {
                  ...admission,
                  completedPercentage: latestStatus?.completedPercentage || null,
                };
              }
            } catch (err) {
              console.error(`Error fetching status for ${admission.projectId}:`, err);
            }
            return {
              ...admission,
              completedPercentage: null,
            };
          })
        );

        // Sort by last 4 digits of projectId (numeric order, descending)
        const sortedData = admissionsWithStatus.sort((a, b) => {
          const numA = getLastFourDigits(a.projectId);
          const numB = getLastFourDigits(b.projectId);
          return numB - numA; // Descending order (highest number first)
        });
        
        // Debug: Log project statuses
        console.log('Project statuses in admissions:', sortedData.map(a => ({ projectId: a.projectId, status: a.status })));
        
        setAdmissions(sortedData);
        const totalPages = Math.ceil(sortedData.length / itemsPerPage);
        setCurrentPage(totalPages > 0 ? totalPages : 1);

      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAdmissions();
  }, [api]);

  // Filters
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const filteredAdmissions = admissions.filter(adm => {
    const { projectId, projectCategory, guide, fromDate, toDate, searchQuery, status } = filters;
    const guide1 = adm.guide1?.toLowerCase() || '';
    const guide2 = adm.guide2?.toLowerCase() || '';

    return (
      (projectId === '' || (adm.projectId && adm.projectId.toString().toLowerCase().includes(projectId.toLowerCase()))) &&
      (projectCategory === '' || adm.projectCategory === projectCategory) &&
      (guide === '' || guide1 === guide.toLowerCase() || guide2 === guide.toLowerCase()) &&
      (fromDate === '' || new Date(adm.deadline) >= new Date(fromDate)) &&
      (toDate === '' || new Date(adm.deadline) <= new Date(toDate)) &&
      (searchQuery === '' || adm.projectName.toLowerCase().includes(searchQuery.toLowerCase())) &&
      (status === '' || (adm.status?.toLowerCase() === status.toLowerCase()))
    );
  }).sort((a, b) => {
    // Sort filtered results by last 4 digits of projectId (numeric order)
    const numA = getLastFourDigits(a.projectId);
    const numB = getLastFourDigits(b.projectId);
    return numB - numA; // Descending order (highest number first)
  });
useEffect(() => {
    if (!filteredAdmissions.length) return;

    const token = localStorage.getItem("jwt_token");
    if (!token) return;

    const timeout = setTimeout(async () => {
      try {
        await fetch(`${api}/api/export-admissions-google-sheet`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ admissions: filteredAdmissions }),
        });
      } catch (err) {
        console.error("Google Sheet Auto Sync Failed", err);
      }
    }, 1500); // debounce

    return () => clearTimeout(timeout);
  }, [filteredAdmissions, api]);

  const totalPages = Math.ceil(filteredAdmissions.length / itemsPerPage);
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentAdmissions = filteredAdmissions.slice(indexOfFirst, indexOfLast);
  const goToPage = (page) => setCurrentPage(page);

  // --- EDIT ---
  const handleEditClick = (admission) => {
    setEditPopup(admission);
    setEditData({
      projectId: admission.projectId,
      projectName: admission.projectName || '',
      studentName1: admission.studentName1 || '',
      phoneNumber1: admission.phoneNumber1 || '',
      totalFees: admission.totalFees || '',
      feesPaid: admission.feesPaid || '',
      guide1: admission.guide1 || '',
      guide2: admission.guide2 || ''
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSave = async () => {
    try {
      const response = await fetch(`${api}/project-admissions/${editData.projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData),
      });
      if (response.ok) {
        setAdmissions(prev => prev.map(a => a.projectId === editData.projectId ? { ...a, ...editData } : a));
        alert('Project details updated!');
        setEditPopup(null);
      } else {
        setError('Failed to update project');
      }
    } catch {
      setError('Error updating project');
    }
  };

  // --- UPDATE STATUS ---
  const handleUpdateClick = async (admission) => {
    try {
      const response = await fetch(`${api}/project-status/${admission.projectId}`);
      
      let latestStatus = {};
      if (response.ok) {
        const data = await response.json();
        latestStatus = Array.isArray(data.status) && data.status.length > 0 ? data.status[0] : {};
      } else {
        // If status doesn't exist, use empty defaults
        console.log('No existing status found, using defaults');
      }

      setUpdatePopup(admission);
      setUpdateData({
        projectId: admission.projectId,
        status: latestStatus.status || 'Pending',
        completedPercentage: latestStatus.completedPercentage || '',
        supportRequired: latestStatus.supportRequired || '',
        anyProblems: latestStatus.anyProblems || '',
        estimatedDateToComplete: latestStatus.estimatedDateToComplete
          ? new Date(latestStatus.estimatedDateToComplete).toISOString().slice(0,10)
          : '',
        dockerPullLink: latestStatus.dockerPullLink || '',
        dockerRunCommand: latestStatus.dockerRunCommand || '',
        githubLink: latestStatus.githubLink || '',
        documentationLink: latestStatus.documentationLink || ''
      });
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Error fetching status:', err);
      // Still open the popup with empty defaults even if there's an error
      setUpdatePopup(admission);
      setUpdateData({
        projectId: admission.projectId,
        status: 'Pending',
        completedPercentage: '',
        supportRequired: '',
        anyProblems: '',
        estimatedDateToComplete: '',
        dockerPullLink: '',
        dockerRunCommand: '',
        githubLink: '',
        documentationLink: ''
      });
      setError(null); // Don't show error, just use defaults
    }
  };

  const handleUpdateChange = (e) => {
    const { name, value } = e.target;
    setUpdateData(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateSave = async () => {
    try {
      // Validate required fields
      const completedPercentage = updateData.completedPercentage 
        ? parseInt(updateData.completedPercentage) 
        : 0;
      
      if (isNaN(completedPercentage) || completedPercentage < 0 || completedPercentage > 100) {
        alert('Please enter a valid completion percentage (0-100)');
        return;
      }

      if (!updateData.supportRequired || updateData.supportRequired.trim() === '') {
        alert('Support Required field is required');
        return;
      }

      if (!updateData.anyProblems || updateData.anyProblems.trim() === '') {
        alert('Any Problems field is required');
        return;
      }

      if (!updateData.estimatedDateToComplete) {
        alert('Estimated Date to Complete is required');
        return;
      }

      const payload = {
        status: updateData.status || 'Pending',
        completedPercentage: completedPercentage,
        supportRequired: updateData.supportRequired.trim(),
        anyProblems: updateData.anyProblems.trim(),
        estimatedDateToComplete: updateData.estimatedDateToComplete,
        dockerPullLink: updateData.dockerPullLink || '',
        dockerRunCommand: updateData.dockerRunCommand || '',
        githubLink: updateData.githubLink || '',
        documentationLink: updateData.documentationLink || ''
      };

      const token = localStorage.getItem("jwt_token");
      const response = await fetch(`${api}/project-status/${updateData.projectId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update status' }));
        throw new Error(errorData.error || 'Failed to update status');
      }

      const data = await response.json();
      
      // Update admissions state with the new status and completedPercentage
      setAdmissions(prev => prev.map(a =>
        a.projectId === updateData.projectId 
          ? { ...a, 
              completedPercentage: completedPercentage,
              status: updateData.status || 'Pending'
            } 
          : a
      ));
      alert('Project progress updated!');
      setUpdatePopup(null);
      setError(null);

    } catch (err) {
      console.error('Error updating progress:', err);
      const errorMessage = err.message || 'Error updating progress';
      alert(errorMessage);
      setError(errorMessage);
    }
  };

  // --- DELETE ---
  const handleDeleteClick = async (admission) => {
    if (!window.confirm(`Delete project ${admission.projectName}?`)) return;
    try {
      const response = await fetch(`${api}/project-admissions/${admission.projectId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        setAdmissions(prev => prev.filter(a => a.projectId !== admission.projectId));
        alert('Project deleted!');
      } else setError('Failed to delete');
    } catch {
      setError('Error deleting project');
    }
  };

  // --- VIEW DETAILS ---
 const handleViewClick = async (admission) => {
  try {
    const response = await fetch(`${api}/project-status/${admission.projectId}`);
    
    if (!response.ok) {
      // If project has no status records or API returns 404
      setViewDetailsPopup([]);
      setError("No updates found for this project.");
      return;
    }

    const data = await response.json();

    // Ensure statusData is always an array
    const statusData = Array.isArray(data.statusList)
      ? data.statusList
      : Array.isArray(data.status)
      ? data.status
      : [];

    if (statusData.length === 0) {
      setViewDetailsPopup([]);
      setError("No updates found for this project.");
      return;
    }

    // Map each status update to timeline item
    const timelineItems = statusData.map((item, index) => ({
      title: item.date ? new Date(item.date).toLocaleDateString() : `Update ${index + 1}`,
      itemData: item // Attach original data safely
    }));

    setViewDetailsPopup(timelineItems);
    setError(null); // Clear previous errors if any

  } catch (error) {
    console.error("Error fetching details:", error);
    setError("Error fetching project timeline");
    setViewDetailsPopup([]);
  }
};

  // --- FEEDBACK/REVIEWS ---
  const handleFeedbackClick = async (admission) => {
    try {
      const token = localStorage.getItem("jwt_token");
      const admissionId = admission._id;
      
      console.log('Opening feedback for admission:', { admissionId, projectId: admission.projectId, admission });
      
      if (!admissionId) {
        console.error('Admission ID not found in:', admission);
        setError('Admission ID not found. Please refresh the page.');
        // Still open popup
        setReviews([]);
        setFeedbackPopup(admission);
        setReviewForm({
          feedback: '',
          reviewerName: '',
          rating: '',
          reviewNumber: '',
          attachment: null
        });
        return;
      }

      // Build headers
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Fetch reviews for this admission
      const response = await fetch(`${api}/project-admissions/${admissionId}/reviews`, {
        method: 'GET',
        headers: headers,
      });

      console.log('Reviews fetch response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Reviews fetched successfully:', data);
        setReviews(data.reviews || []);
        setFeedbackPopup(admission);
        setReviewForm({
          feedback: '',
          reviewerName: '',
          rating: '',
          reviewNumber: '',
          attachment: null
        });
        setError(null); // Clear any previous errors
      } else {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: errorText || 'Failed to fetch reviews' };
        }
        console.error('Failed to fetch reviews:', response.status, errorData);
        setError(errorData.message || `Failed to fetch reviews (Status: ${response.status})`);
        // Still open the popup with empty reviews
        setReviews([]);
        setFeedbackPopup(admission);
        setReviewForm({
          feedback: '',
          reviewerName: '',
          rating: '',
          reviewNumber: '',
          attachment: null
        });
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setError('Error fetching reviews: ' + (error.message || 'Network error'));
      // Still open the popup with empty reviews
      setReviews([]);
      setFeedbackPopup(admission);
      setReviewForm({
        feedback: '',
        reviewerName: '',
        rating: '',
        reviewNumber: '',
        attachment: null
      });
    }
  };

  const handleReviewFormChange = (e) => {
    const { name, value } = e.target;
    setReviewForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setReviewForm(prev => ({ ...prev, attachment: e.target.files[0] }));
  };

  const handleAddReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.feedback) {
      alert('Feedback is required');
      return;
    }

    if (reviews.length >= 5) {
      alert('Maximum 5 reviews allowed per project');
      return;
    }

    try {
      const token = localStorage.getItem("jwt_token");
      const formData = new FormData();
      formData.append('feedback', reviewForm.feedback);
      formData.append('reviewerName', reviewForm.reviewerName);
      formData.append('rating', reviewForm.rating);
      if (reviewForm.reviewNumber) {
        formData.append('reviewNumber', reviewForm.reviewNumber);
      }
      if (reviewForm.attachment) {
        formData.append('attachment', reviewForm.attachment);
      }

      const response = await fetch(`${api}/project-admissions/${feedbackPopup._id}/reviews`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setReviews(data.admission.reviews || []);
        setReviewForm({
          feedback: '',
          reviewerName: '',
          rating: '',
          reviewNumber: '',
          attachment: null
        });
        alert('Review added successfully');
        
        // Refresh admissions list
        const refreshResponse = await fetch(`${api}/project-admissions`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          setAdmissions(refreshData.sort((a, b) => b.projectId - a.projectId));
        }
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to add review');
      }
    } catch (error) {
      console.error('Error adding review:', error);
      alert('Error adding review');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Delete this review?')) return;

    try {
      const token = localStorage.getItem("jwt_token");
      const response = await fetch(`${api}/project-admissions/${feedbackPopup._id}/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReviews(data.admission.reviews || []);
        alert('Review deleted successfully');
      } else {
        alert('Failed to delete review');
      }
    } catch (error) {
      console.error('Error deleting review:', error);
      alert('Error deleting review');
  }
};

  const handleClosePopup = () => {
    setEditPopup(null);
    setUpdatePopup(null);
    setViewDetailsPopup([]);
    setFeedbackPopup(null);
    setReviews([]);
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  return (
    <div className="project-admission-container">
      <h1>Project Admissions</h1>
      <p>Total Projects: {filteredAdmissions.length}</p>

      {/* Filters */}
      <div className="filters-container">
        <div className="filters-grid">
          <div className="filter-item">
            <label>Project ID</label>
            <input
              type="text"
              name="projectId"
              placeholder="Search Project ID"
              value={filters.projectId}
              onChange={handleFilterChange}
              className="filter-input"
            />
          </div>

          <div className="filter-item">
            <label>Project Title</label>
            <input
              type="text"
              name="searchQuery"
              placeholder="Search by Project Title"
              value={filters.searchQuery}
              onChange={handleFilterChange}
              className="filter-input"
            />
          </div>

          <div className="filter-item">
            <label>Category</label>
            <select
              name="projectCategory"
              value={filters.projectCategory}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">All Categories</option>
              <option value="Blockchain">Blockchain</option>
              <option value="Web Designing">Web Designing</option>
              <option value="Machine Learning">Machine Learning</option>
              <option value="Artificial Intelligence">AI</option>
              <option value="Deep Learning">Deep Learning</option>
              <option value="Cyber Security">Cyber Security</option>
              <option value="Networking">Networking</option>
              <option value="Cloud Computing">Cloud Computing</option>
              <option value="IoT">IoT</option>
              <option value="Android">Android</option>
            </select>
          </div>

          <div className="filter-item">
            <label>Guide</label>
            <select
              name="guide"
              value={filters.guide}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">All Guides</option>
              {guidesList.map((g, i) => (
                <option key={i} value={g}>{g}</option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label>Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Completed">Completed</option>
              <option value="Delivered">Delivered</option>
            </select>
          </div>

          <div className="filter-item">
            <label>From Date</label>
            <input
              type="date"
              name="fromDate"
              value={filters.fromDate}
              onChange={handleFilterChange}
              className="filter-input"
            />
          </div>

          <div className="filter-item">
            <label>To Date</label>
            <input
              type="date"
              name="toDate"
              value={filters.toDate}
              onChange={handleFilterChange}
              className="filter-input"
            />
          </div>

          <div className="filter-item">
            <label>Items Per Page</label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              className="filter-select"
            >
              {[10, 20, 30, 40, 50].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          {(filters.projectId || filters.searchQuery || filters.projectCategory || filters.guide || filters.status || filters.fromDate || filters.toDate) && (
            <div className="filter-item filter-clear-all">
              <button
                onClick={() => {
                  setFilters({
                    projectId: '',
                    projectCategory: '',
                    guide: '',
                    fromDate: '',
                    toDate: '',
                    searchQuery: '',
                    status: '',
                  });
                  setCurrentPage(1);
                }}
                className="clear-all-filters-btn"
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      {filteredAdmissions.length === 0 ? (
        <p className="project-admission-no-data">NO DATA</p>
      ) : (
        <>
          <div className="table-wrapper">
          <table className="project-admission-table">
            <thead>
              <tr>
                <th>Project ID</th>
                <th>Project Name</th>
                <th>Student</th>
                <th>Phone</th>
                <th>Total Fees</th>
                <th>Fees Paid</th>
                <th>Guide 1</th>
                
                <th>Deadline</th>
                <th>Status</th>
                <th>Completed %</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentAdmissions.map(adm => (
                <tr key={adm._id}>
                  <td>{adm.projectId}</td>
                  <td>{adm.projectName}</td>
                  <td>{adm.studentName1}</td>
                 <td>
  {adm.phoneNumber1 ? (
    <a
      href={`https://wa.me/${adm.phoneNumber1}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-green-600 underline"
    >
      {adm.phoneNumber1}
    </a>
  ) : (
    '-'
  )}
</td>
                  <td>{adm.totalFees}</td>
                  <td>{adm.feesPaid}</td>
                  <td>{adm.guide1}</td>
                
                  <td>{adm.deadline ? new Date(adm.deadline).toLocaleDateString() : '-'}</td>
                  <td>{adm.status || '-'}</td>
                  <td>
                    {adm.completedPercentage !== null && adm.completedPercentage !== undefined
                      ? `${adm.completedPercentage}%`
                      : '-'}
                  </td>
                  <td className="flex gap-2 justify-center">
  <button onClick={() => handleEditClick(adm)} title="Edit" className="text-blue-500 hover:text-blue-700">
    <FaEdit />
  </button>

  <button onClick={() => handleUpdateClick(adm)} title="Update" className="text-green-500 hover:text-green-700">
    <FaSyncAlt />
  </button>

  <button onClick={() => handleViewClick(adm)} title="View" className="text-yellow-500 hover:text-yellow-700">
    <FaEye />
  </button>

  <button onClick={() => handleFeedbackClick(adm)} title="Feedback/Reviews" className="text-purple-500 hover:text-purple-700">
    <FaStar />
  </button>

  <button onClick={() => handleDeleteClick(adm)} title="Delete" className="text-red-500 hover:text-red-700">
    <FaTrash />
  </button>
</td>

                </tr>
            ))}
          </tbody>
        </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            {Array.from({ length: totalPages }, (_, i) => (
              <button key={i} onClick={() => goToPage(i + 1)} className={currentPage === i+1 ? 'active':''}>{i+1}</button>
            ))}
          </div>
        </>
      )}

      {/* EDIT POPUP */}
      {editPopup && (
        <div className="popup">
          <div className="popup-content">
            <h2>Edit Project</h2>
            <label>Project Name: <input type="text" name="projectName" value={editData.projectName} onChange={handleEditChange} /></label>
            <label>Student: <input type="text" name="studentName1" value={editData.studentName1} onChange={handleEditChange} /></label>
            <label>Phone: <input type="text" name="phoneNumber1" value={editData.phoneNumber1} onChange={handleEditChange} /></label>
            <label>Total Fees: <input type="number" name="totalFees" value={editData.totalFees} onChange={handleEditChange} /></label>
            <label>Fees Paid: <input type="number" name="feesPaid" value={editData.feesPaid} onChange={handleEditChange} /></label>
            <label>Guide 1: 
              <select name="guide1" value={editData.guide1} onChange={handleEditChange}>
                <option value="">Select Guide 1</option>
                {guidesList.map((g,i) => <option key={i} value={g}>{g}</option>)}
              </select>
            </label>
            <label>Guide 2: 
              <select name="guide2" value={editData.guide2} onChange={handleEditChange}>
                <option value="">Select Guide 2</option>
                {guidesList.map((g,i) => <option key={i} value={g}>{g}</option>)}
              </select>
            </label>
            <div className="popup-actions">
              <button onClick={handleClosePopup}>Close</button>
              <button onClick={handleEditSave}>Save</button>
            </div>
          </div>
        </div>
      )}

     {updatePopup && (
  <div className="popup fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
    <div className="popup-content bg-white p-6 rounded-2xl shadow-lg w-[90%] max-w-2xl overflow-y-auto max-h-[90vh]">
      <h2 className="text-xl font-bold mb-4 text-center text-blue-600">Update Project Progress</h2>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="flex flex-col">
          <span className="font-semibold mb-1">Project Status <span className="text-red-500">*</span></span>
          <select
            name="status"
            value={updateData.status || 'Pending'}
            onChange={handleUpdateChange}
            required
            className="border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="Pending">Pending</option>
            <option value="In Review">In Review</option>
            <option value="Completed">Completed</option>
            <option value="Delivered">Delivered</option>
          </select>
        </label>

        <label className="flex flex-col">
          <span className="font-semibold mb-1">Completed Percentage <span className="text-red-500">*</span></span>
          <input
            type="number"
            name="completedPercentage"
            value={updateData.completedPercentage}
            onChange={handleUpdateChange}
            min="0"
            max="100"
            required
            className="border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="e.g. 70"
          />
        </label>

        <label className="flex flex-col">
          <span className="font-semibold mb-1">Estimated Completion Date <span className="text-red-500">*</span></span>
          <input
            type="date"
            name="estimatedDateToComplete"
            value={updateData.estimatedDateToComplete}
            onChange={handleUpdateChange}
            required
            className="border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </label>

        <label className="flex flex-col sm:col-span-2">
          <span className="font-semibold mb-1">Remarks / Support Required <span className="text-red-500">*</span></span>
          <textarea
            name="supportRequired"
            value={updateData.supportRequired}
            onChange={handleUpdateChange}
            rows="2"
            required
            className="border p-2 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Enter any remarks or support needed"
          />
        </label>

        <label className="flex flex-col sm:col-span-2">
          <span className="font-semibold mb-1">Any Problems <span className="text-red-500">*</span></span>
          <textarea
            name="anyProblems"
            value={updateData.anyProblems}
            onChange={handleUpdateChange}
            rows="2"
            required
            className="border p-2 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Mention issues if any"
          />
        </label>

        <label className="flex flex-col sm:col-span-2">
          <span className="font-semibold mb-1">Docker Pull Link</span>
          <input
            type="text"
            name="dockerPullLink"
            value={updateData.dockerPullLink}
            onChange={handleUpdateChange}
            className="border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Enter Docker Pull Link"
          />
        </label>

        <label className="flex flex-col sm:col-span-2">
          <span className="font-semibold mb-1">Docker Run Command</span>
          <input
            type="text"
            name="dockerRunCommand"
            value={updateData.dockerRunCommand}
            onChange={handleUpdateChange}
            className="border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Enter Docker Run Command"
          />
        </label>

        <label className="flex flex-col sm:col-span-2">
          <span className="font-semibold mb-1">GitHub Link</span>
          <input
            type="text"
            name="githubLink"
            value={updateData.githubLink}
            onChange={handleUpdateChange}
            className="border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="https://github.com/yourproject"
          />
        </label>

        <label className="flex flex-col sm:col-span-2">
          <span className="font-semibold mb-1">Documentation Link</span>
          <input
            type="text"
            name="documentationLink"
            value={updateData.documentationLink}
            onChange={handleUpdateChange}
            className="border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="https://docs.google.com/..."
          />
        </label>
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button
          onClick={handleClosePopup}
          className="bg-gray-300 hover:bg-gray-400 text-black px-4 py-2 rounded-md transition duration-200"
        >
          Close
        </button>
        <button
          onClick={handleUpdateSave}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition duration-200"
        >
          Update
        </button>
      </div>
    </div>
  </div>
)}

      {/* VIEW POPUP */}
 {Array.isArray(viewDetailsPopup) && viewDetailsPopup.length > 0 && (
  <div className="popup fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
    <div className="popup-content bg-white p-6 rounded-2xl shadow-lg w-[90%] max-w-2xl overflow-y-auto max-h-[90vh]">
      <h2 className="text-xl font-semibold mb-4 text-yellow-600 text-center">
        Project Progress Timeline
      </h2>

      <div className="space-y-6">
        {viewDetailsPopup.every(t => !t.itemData) ? (
          <p className="text-center text-gray-500">No updates found for this project.</p>
        ) : (
          viewDetailsPopup.map((timeline, index) => {
            const item = timeline.itemData || {};
            return (
              <div key={index} className="border p-4 rounded-lg shadow-sm bg-gray-50">
                <h3 className="font-bold text-lg mb-2">{timeline.title}</h3>
                <div className="mb-2 flex items-center gap-3">
                  <p><strong>Status:</strong> 
                    <span className={`ml-2 px-2 py-1 rounded text-sm font-semibold ${
                      item.status === 'Complete' ? 'bg-green-100 text-green-800' :
                      item.status === 'In Review' ? 'bg-blue-100 text-blue-800' :
                      item.status === 'On Hold' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {item.status || 'Pending'}
                    </span>
                  </p>
                  <p><strong>Progress:</strong> <span className="font-semibold text-blue-600">{item.completedPercentage ?? 0}%</span></p>
                </div>
                <p><strong>Support:</strong> {item.supportRequired || 'N/A'}</p>
                <p><strong>Problems:</strong> {item.anyProblems || 'N/A'}</p>
                <p>
                  <strong>Estimated Completion:</strong>{' '}
                  {item.estimatedDateToComplete
                    ? new Date(item.estimatedDateToComplete).toLocaleDateString()
                    : 'N/A'}
                </p>
                <p>
                  <strong>GitHub:</strong>{' '}
                  {item.githubLink ? (
                    <a
                      href={item.githubLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      {item.githubLink}
                    </a>
                  ) : 'N/A'}
                </p>
                <p>
                  <strong>Docker Pull:</strong>{' '}
                  {item.dockerPullLink ? (
                    <a
                      href={item.dockerPullLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      {item.dockerPullLink}
                    </a>
                  ) : 'N/A'}
                </p>
                <p><strong>Docker Run Command:</strong> {item.dockerRunCommand || 'N/A'}</p>
                <p>
                  <strong>Documentation:</strong>{' '}
                  {item.documentationLink ? (
                    <a
                      href={item.documentationLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      {item.documentationLink}
                    </a>
                  ) : 'N/A'}
                </p>
              </div>
            );
          })
        )}
      </div>

      <div className="flex justify-center mt-6">
        <button
          onClick={handleClosePopup}
          className="bg-gray-300 hover:bg-gray-400 text-black px-4 py-2 rounded-md transition"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}

      {/* FEEDBACK/REVIEWS POPUP */}
      {feedbackPopup && (
        <div className="popup fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="popup-content bg-white p-6 rounded-2xl shadow-lg w-[90%] max-w-4xl overflow-y-auto max-h-[90vh]">
            <h2 className="text-xl font-bold mb-4 text-center text-purple-600">
              Feedback/Reviews for {feedbackPopup.projectName}
            </h2>
            <p className="text-center text-sm text-gray-600 mb-4">
              Project ID: {feedbackPopup.projectId} | Reviews: {reviews.length}/5
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 rounded-lg">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            {/* Add Review Form */}
            {reviews.length < 5 && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-semibold mb-3">Add New Review</h3>
                <form onSubmit={handleAddReview}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <label className="flex flex-col">
                      <span className="font-semibold mb-1">Review Number (1-5) <span className="text-red-500">*</span></span>
                      <input
                        type="number"
                        name="reviewNumber"
                        value={reviewForm.reviewNumber}
                        onChange={handleReviewFormChange}
                        min="1"
                        max="5"
                        className="border p-2 rounded-md"
                        placeholder="Auto-assigned if empty"
                      />
                    </label>

                    <label className="flex flex-col">
                      <span className="font-semibold mb-1">Reviewer Name</span>
                      <input
                        type="text"
                        name="reviewerName"
                        value={reviewForm.reviewerName}
                        onChange={handleReviewFormChange}
                        className="border p-2 rounded-md"
                        placeholder="Enter reviewer name"
                      />
                    </label>

                    <label className="flex flex-col">
                      <span className="font-semibold mb-1">Rating (1-5)</span>
                      <select
                        name="rating"
                        value={reviewForm.rating}
                        onChange={handleReviewFormChange}
                        className="border p-2 rounded-md"
                      >
                        <option value="">Select Rating</option>
                        {[1, 2, 3, 4, 5].map(r => (
                          <option key={r} value={r}>{r} Star{r > 1 ? 's' : ''}</option>
                        ))}
                      </select>
                    </label>

                    <label className="flex flex-col">
                      <span className="font-semibold mb-1">Attachment</span>
                      <input
                        type="file"
                        name="attachment"
                        onChange={handleFileChange}
                        className="border p-2 rounded-md"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      />
                    </label>

                    <label className="flex flex-col sm:col-span-2">
                      <span className="font-semibold mb-1">Feedback <span className="text-red-500">*</span></span>
                      <textarea
                        name="feedback"
                        value={reviewForm.feedback}
                        onChange={handleReviewFormChange}
                        rows="4"
                        required
                        className="border p-2 rounded-md resize-none"
                        placeholder="Enter feedback/review..."
                      />
                    </label>
                  </div>

                  <div className="flex justify-end gap-3 mt-4">
                    <button
                      type="submit"
                      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md"
                    >
                      Add Review
                    </button>
                  </div>
                </form>
              </div>
            )}

            {reviews.length >= 5 && (
              <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 rounded-lg">
                <p className="text-yellow-800 text-sm">Maximum 5 reviews reached. Delete a review to add a new one.</p>
              </div>
            )}

            {/* Reviews List */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-3">Reviews ({reviews.length}/5)</h3>
              {reviews.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No reviews yet. Add your first review above.</p>
              ) : (
                <div className="space-y-4">
                  {reviews
                    .sort((a, b) => a.reviewNumber - b.reviewNumber)
                    .map((review) => (
                      <div key={review._id} className="border p-4 rounded-lg shadow-sm bg-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <span className="font-bold text-purple-600">Review #{review.reviewNumber}</span>
                            {review.rating && (
                              <div className="flex items-center gap-1 mt-1">
                                {[...Array(5)].map((_, i) => (
                                  <FaStar
                                    key={i}
                                    className={i < review.rating ? 'text-yellow-400' : 'text-gray-300'}
                                    size={14}
                                  />
                                ))}
                                <span className="ml-2 text-sm text-gray-600">({review.rating}/5)</span>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleDeleteReview(review._id)}
                            className="text-red-500 hover:text-red-700 text-sm"
                            title="Delete Review"
                          >
                            <FaTrash />
                          </button>
                        </div>
                        
                        {review.reviewerName && (
                          <p className="text-sm text-gray-600 mb-2">
                            <strong>Reviewer:</strong> {review.reviewerName}
                          </p>
                        )}
                        
                        <p className="text-sm text-gray-500 mb-2">
                          <strong>Date:</strong> {new Date(review.reviewDate).toLocaleDateString()}
                        </p>
                        
                        <p className="mb-2">{review.feedback}</p>
                        
                        {review.attachment && (
                          <div className="mt-2">
                            <a
                              href={`${api.replace('/api', '') || 'http://localhost:5000'}/uploads/${review.attachment}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                            >
                              <FaFileUpload /> {review.attachmentName || 'View Attachment'}
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>

            <div className="flex justify-center mt-6">
              <button
                onClick={handleClosePopup}
                className="bg-gray-300 hover:bg-gray-400 text-black px-4 py-2 rounded-md transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default JoinedAdmission;
